import { MusicalNoteIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Handle, Position, type NodeProps } from '@xyflow/react';

import AudioPlayer from '../../../common/AudioPlayer';
import CanvasPreviewImage from './CanvasPreviewImage';
import { getAssetMainImageUrl, getAssetVoiceAudioUrl } from '../referenceMedia';
import type { ClipCanvasNode, SettingAssetNodeData } from '../types';

type SettingAssetNodeProps = NodeProps<ClipCanvasNode>;

export default function SettingAssetNode({ id, data }: SettingAssetNodeProps) {
  if (data.kind !== 'setting-asset') return null;
  const nodeData = data as SettingAssetNodeData;
  const { asset, imageUrl } = nodeData;
  const mainImageUrl = getAssetMainImageUrl(asset, imageUrl);
  const audioUrl = getAssetVoiceAudioUrl(asset, nodeData.audioUrl);

  return (
    <div className="clip-canvas-node clip-canvas-node--setting-asset">
      {mainImageUrl && (
        <Handle
          id="reference:image"
          type="source"
          position={Position.Right}
          className="clip-canvas-setting-node__handle clip-canvas-setting-node__handle--image"
          title="图片输出"
        />
      )}
      {audioUrl && (
        <Handle
          id="reference:audio"
          type="source"
          position={Position.Right}
          className="clip-canvas-setting-node__handle clip-canvas-setting-node__handle--audio"
          title="音频输出"
        />
      )}
      <div className="clip-canvas-node__topline">
        <div className="clip-canvas-node__title clip-canvas-node__title--inline">{asset.name}</div>
        <button
          type="button"
          className="clip-canvas-node__icon-button clip-canvas-node__icon-button--danger nodrag nopan"
          onClick={(event) => {
            event.stopPropagation();
            void nodeData.actions?.onDeleteSettingNode?.(id);
          }}
          aria-label={`删除设定 ${asset.name}`}
          title={`删除设定 ${asset.name}`}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="clip-canvas-setting-section">
        <div className="clip-canvas-setting-section__label">
          <PhotoIcon className="clip-canvas-setting-section__icon" />
          <span>主图</span>
        </div>
        <div className="clip-canvas-setting-preview">
          {mainImageUrl ? (
            <CanvasPreviewImage src={mainImageUrl} alt={asset.name} />
          ) : (
            <div className="clip-canvas-setting-preview__empty">暂无设定图</div>
          )}
        </div>
      </div>

      <div className="clip-canvas-setting-section">
        <div className="clip-canvas-setting-section__label">
          <MusicalNoteIcon className="clip-canvas-setting-section__icon" />
          <span>配音</span>
        </div>
        {audioUrl ? (
          <div className="clip-canvas-setting-audio nodrag nopan nowheel">
            <AudioPlayer src={audioUrl} className="w-full h-8" />
          </div>
        ) : (
          <div className="clip-canvas-setting-audio clip-canvas-setting-audio--empty">暂无配音</div>
        )}
      </div>
    </div>
  );
}
